import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Inbox,
  ArrowRight,
  Clock,
  CheckCircle2,
  Eye,
  Share2,
} from "lucide-react";
import AdvisorLayout from "../components/layout/AdvisorLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function AdvisorDashboard() {
  const { user, selectClient } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [clients, setClients] = useState([]);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c, r] = await Promise.all([
        api.get("/pfa/me"),
        api.get("/pfa/clients"),
        api.get("/pfa/requests?status=pending"),
      ]);
      setProfile(p.data);
      setClients(c.data.clients || []);
      setPending((r.data.requests || []).length);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const select = (conn) =>
    selectClient({
      id: conn.client_id,
      name: conn.client_name,
      modules: conn.modules || {},
      permission: conn.permission_level || "view",
    });

  // Approach A — full-page client shell (takes over the whole app chrome).
  const openFullView = (conn) => {
    select(conn);
    const m = conn.modules || {};
    const first = m.dashboard
      ? "/dashboard"
      : m.assets
        ? "/dashboard/assets"
        : m.liabilities
          ? "/dashboard/liabilities"
          : m.insurance
            ? "/dashboard/insurance"
            : m.goals
              ? "/dashboard/goals"
              : m.documents
                ? "/dashboard/documents"
                : "/dashboard";
    navigate(first);
  };

  // Approach B — stay in the advisor console, render the client's data inline.
  const openWorkspace = (conn) => {
    select(conn);
    navigate("/advisor/workspace");
  };

  return (
    <AdvisorLayout>
      <div className="max-w-6xl mx-auto">
        {/* GREETING */}
        <div className="mb-6">
          <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-white">
            Welcome, {user?.name || "Advisor"}
          </h1>
          <p className="text-sm text-slate-500">
            Select a client to step into their workspace, or share your code to
            grow your book.
          </p>
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400">Loading…</div>
        ) : (
          <>
            {/* STATS */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
              <Stat icon={Users} label="Connected clients" value={clients.length} />
              <Stat icon={Inbox} label="Pending approvals" value={pending} accent="amber" />
              <ShareCodeCard code={profile?.advisor_code} />
            </div>

            {/* CLIENTS */}
            <div className="mb-3 flex items-center gap-2">
              <Users className="h-4 w-4 text-slate-400" />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
                Your clients
              </h2>
            </div>

            {clients.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-slate-300 dark:border-slate-700 p-10 text-center">
                <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-500">
                  <Share2 className="h-5 w-5" />
                </div>
                <p className="text-sm text-slate-500">
                  No clients yet. Share your advisor code{" "}
                  <span className="font-mono font-semibold text-slate-700 dark:text-slate-300">
                    {profile?.advisor_code}
                  </span>{" "}
                  so clients can connect with you.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {clients.map((c) => (
                  <ClientCard
                    key={c.id}
                    conn={c}
                    onWorkspace={() => openWorkspace(c)}
                    onFullView={() => openFullView(c)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </AdvisorLayout>
  );
}

function Stat({ icon: Icon, label, value, accent = "emerald" }) {
  const tone =
    accent === "amber"
      ? "bg-amber-50 dark:bg-amber-500/10 text-amber-600"
      : "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600";
  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 flex items-center gap-4">
      <div className={`grid h-11 w-11 place-items-center rounded-2xl ${tone}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-2xl font-bold text-slate-900 dark:text-white">
          {value}
        </div>
        <div className="text-xs text-slate-500">{label}</div>
      </div>
    </div>
  );
}

function ShareCodeCard({ code }) {
  return (
    <div className="col-span-2 sm:col-span-1 rounded-2xl bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-500/20 p-5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 mb-1">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
        Share to connect
      </div>
      <div className="font-mono text-lg font-bold text-slate-900 dark:text-white">
        {code || "—"}
      </div>
      <div className="text-[11px] text-slate-400 mt-1">Never expires</div>
    </div>
  );
}

function ClientCard({ conn, onWorkspace, onFullView }) {
  const modules = Object.entries(conn.modules || {})
    .filter(([, v]) => v)
    .map(([k]) => k);
  const canSuggest = conn.permission_level === "suggest";
  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 flex flex-col">
      <div className="flex items-start gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 font-bold">
          {conn.client_name?.[0]?.toUpperCase() || "C"}
        </span>
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 dark:text-white truncate">
            {conn.client_name}
          </div>
          <div className="text-[11px] flex items-center gap-1 text-slate-500">
            {canSuggest ? (
              <>
                <CheckCircle2 className="h-3 w-3 text-emerald-500" /> View + Edit
              </>
            ) : (
              <>
                <Eye className="h-3 w-3" /> View only
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {modules.length ? (
          modules.map((m) => (
            <span
              key={m}
              className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 capitalize"
            >
              {m}
            </span>
          ))
        ) : (
          <span className="text-[11px] text-slate-400">No modules shared</span>
        )}
      </div>

      {conn.pending_requests > 0 && (
        <div className="mt-3 inline-flex items-center gap-1 text-[11px] text-amber-600">
          <Clock className="h-3 w-3" />
          {conn.pending_requests} awaiting client approval
        </div>
      )}

      {/* Two ways to open the client — for side-by-side comparison. */}
      <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 grid grid-cols-2 gap-2">
        <button
          onClick={onWorkspace}
          className="flex items-center justify-center gap-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-xs font-semibold px-2 py-2 hover:bg-emerald-100 dark:hover:bg-emerald-500/20"
        >
          Workspace
        </button>
        <button
          onClick={onFullView}
          className="flex items-center justify-center gap-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-2 py-2"
        >
          Full view <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
